import _ from 'lodash';
import {
 addHours, fromUnixTime, parse,
} from 'date-fns';
import {
 useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { qlog, qapi, V1_FEATURES } from './v1_adapter';
import generateUuid from './generateUuid';

import {
 computeFieldHints, getInitialState, isReadyForDraftSaving, validationErrors,
} from './schema';
import {
  serializeRequestType,
  serializeRequestSubtype,
  serializeLogistics,
  serializeLinks,
  serializeEquipmentNotes,
  serializeDeadline,
  serializeSchedule,
  serializeNotification,
  deserializeRequestType,
  deserializeRequestSubtype,
  deserializeLogistics,
  deserializeLinks,
  deserializeDeadline,
  deserializeSchedule,
  deserializeNotification,
  deserializeSimpleFields,
} from './schema-mappings';

const SHOW_LOGISTICS_DETAILS_WHEN_DISTRIBUTION = new Set(['radio', 'podcast', 'video', 'event']);

// This is schema version that should be increased every time a backwards
// incompatible change to new source request defaults data is introduced.
// Increasing it will reset the state to a safe blank slate.
const LOCAL_STORAGE_SCHEMA_VERSION = 1;
const LOCAL_STORAGE_PERSISTED_DEFAULTS_TAG = `newSourceRequestDefaults::${LOCAL_STORAGE_SCHEMA_VERSION}`;
const LOCAL_STORAGE_PERSISTED_DEFAULTS_KEYS = ['lookingFor', 'mediaOutletId', 'distribution', 'eventType', 'personType'];

// =============================================================================
// SERIALIZATION: Frontend State -> Rails API
// =============================================================================

export function serializeSourceRequest(derivedState) {
  // Converts form state to API request format using centralized schema mappings
  // Explicit contract - maps directly to Interact::RequestPolicy::ALLOWLIST_ATTRS
  // This ensures we only send fields the API accepts, preventing noise and data leakage
  return {
    backlink_opportunity: derivedState.backlinkOpportunity,
    deadline_at: serializeDeadline(derivedState),
    details: derivedState.details,
    distribute_externally: derivedState.distributeExternally,
    equipment_notes: serializeEquipmentNotes(derivedState),
    links: serializeLinks(derivedState),
    logistics_details: serializeLogistics(derivedState),
    media_outlet_id: derivedState.mediaOutletId ? parseInt(derivedState.mediaOutletId, 10) : null,
    paid: derivedState.paid,
    free_to_pitch: derivedState.freeToPitch,
    pitch_notify_mode: serializeNotification(derivedState),
    request_sub_type: serializeRequestSubtype(derivedState),
    request_type: serializeRequestType(derivedState),
    scheduled_at: serializeSchedule(derivedState),
    title: derivedState.title,
    // Note: deduplication_key added conditionally in saveRequest()
  };
}

// =============================================================================
// DESERIALIZATION: Rails API -> Frontend State
// =============================================================================

export function deserializeSourceRequest(apiResponse) {
  // Converts API response to form state using centralized schema mappings
  // Handles both direct model responses and JSON:API formatted responses
  const attributes = apiResponse.attributes || apiResponse;

  // Validation: Ensure required fields exist (only for real API responses)
  // Test helpers may use partial responses, so only validate if response looks like real API data
  const isRealApiResponse = (attrs) => {
    const realApiIndicators = ['request_type', 'request_sub_type', 'title', 'details'];
    return realApiIndicators.every((field) => field in attrs);
  };

  if (isRealApiResponse(attributes)) {
    const requiredFields = [
      'request_type',
      'request_sub_type',
      'paid',
      'backlink_opportunity',
      'pitch_notify_mode',
      'logistics_details',
      'links',
      'distribute_externally',
      'equipment_notes',
      'deadline_at',
      'scheduled_at',
      'title',
      'details',
    ];

    requiredFields.forEach((field) => {
      if (!(field in attributes)) {
        throw new Error(
          `Deserialization error: Missing required field '${field}' from API response. `
            + 'This likely indicates the API schema has changed. '
            + `Response had keys: ${Object.keys(attributes).sort().join(', ')}`,
        );
      }
    });
  }

  const simpleFields = deserializeSimpleFields(attributes);

  // Set default values for optional fields that might be missing from API response
  // This ensures backward compatibility with partial API responses
  if (simpleFields.backlinkOpportunity === undefined) {
    simpleFields.backlinkOpportunity = false;
  }
  if (simpleFields.paid === undefined) {
    simpleFields.paid = false;
  }

  // Decompose complex fields using schema mappers
  const { lookingFor, personType, speakerRequest } = deserializeRequestType(attributes.request_type);
  const { distribution, eventType } = deserializeRequestSubtype(attributes.request_sub_type);
  const {
 logisticsVideo, logisticsInPerson, logisticsLive, logisticsPreRecorded, logisticsRemote,
} = deserializeLogistics(attributes.logistics_details);
  const logisticsUrl = deserializeLinks(attributes.links);
  const { deadline, customDeadlineDateTime } = deserializeDeadline(attributes.deadline_at, attributes.scheduled_at);
  const { schedule, customScheduleDateTime } = deserializeSchedule(attributes.scheduled_at);
  const notification = deserializeNotification(attributes.pitch_notify_mode);

  return {
    ...simpleFields,
    sourceRequestId: attributes.id,
    slug: attributes.slug || null,
    migrateId: attributes.migrate_id || null,
    requestStatus: attributes.status || null,
    lookingFor,
    personType,
    speakerRequest,
    distribution,
    eventType,
    logisticsUrl,
    logisticsVideo,
    logisticsInPerson,
    logisticsLive,
    logisticsPreRecorded,
    logisticsRemote,
    deadline,
    customDeadlineDateTime,
    notification,
    schedule,
    customScheduleDateTime,
  };
}

// =============================================================================
// STATE MANAGEMENT
// =============================================================================

/**
 * Safely parse persisted defaults from localStorage
 * Returns empty object if parsing fails or input is null/undefined
 *
 * @param {string|null} jsonString - JSON string from localStorage
 * @returns {Object} Parsed object or {}
 */
export function parsePersistedDefaults(jsonString) {
  if (!jsonString) {
    return {};
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    qlog.warn('Failed to parse persisted defaults from localStorage:', error);
    return {};
  }
}

export function populateDerivedState(state) {
  // First, apply visibility rules to get a clean filtered state
  // This handles toggle-back UX: raw state preserves hidden values, derived state shows only visible ones
  const derived = { ...state };

  // Visibility Rule 1: If lookingFor=product, blank all person-specific fields
  if (state.lookingFor === 'product') {
    derived.personType = 'expert';
    derived.eventType = null;
  }

  const isEventDistribution = derived.distribution === 'event';
  if (!isEventDistribution) derived.speakerRequest = false;
  derived.manOnTheStreet = state.lookingFor === 'person' && state.personType === 'man_on_the_street';
  if (!isEventDistribution || !derived.speakerRequest) derived.eventType = null;

  // Visibility Rule 3: If distribution doesn't support logistics, blank logistics
  if (!SHOW_LOGISTICS_DETAILS_WHEN_DISTRIBUTION.has(state.distribution)) {
    derived.logisticsVideo = false;
    derived.logisticsInPerson = false;
    derived.logisticsLive = false;
    derived.logisticsPreRecorded = false;
    derived.logisticsRemote = false;
    derived.logisticsUrl = '';
    derived.equipmentNotes = '';
  }

  // Now compute derived properties (computed deadline + logistics visibility)
  const {
 timeNow, customDeadlineDateTime, schedule, customScheduleDateTime,
} = state;

  // Determine base time for deadline calculation
  // If scheduled, deadline is relative to scheduled publish time; otherwise relative to now
  let baseTime;
  if (schedule === 'custom' && customScheduleDateTime) {
    // Scheduled post: deadline relative to scheduled publish time
    baseTime = parse(customScheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
  } else {
    // Immediate post: deadline relative to now
    baseTime = fromUnixTime(timeNow);
  }

  let deadlineAt = null;
  switch (state.deadline) {
    case '24hrs':
      deadlineAt = addHours(baseTime, 24);
      break;
    case '72hrs':
      deadlineAt = addHours(baseTime, 72);
      break;
    case '7days':
      deadlineAt = addHours(baseTime, 168);
      break;
    case 'custom':
      if (customDeadlineDateTime) {
        deadlineAt = parse(customDeadlineDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
      }
      break;
    default:
      throw new Error(`Unexpected deadline value: ${state.deadline}`);
  }

  // Compute field hints using schema
  const fieldHints = computeFieldHints(state);

  // Return merged result: all raw fields + visibility-filtered fields + computed properties + field hints
  return {
    ...derived,
    ...fieldHints,
    showLogisticDetails: SHOW_LOGISTICS_DETAILS_WHEN_DISTRIBUTION.has(derived.distribution),
    deadlineAt,
    freeToPitch: state.paid || derived.manOnTheStreet,
  };
}

/**
 * Compute next state with validation, version increment, and error handling
 * Pure function - no side effects, no React state
 *
 * @param {Object} prevState - Previous state
 * @param {Object|Function} update - New values or function to compute them
 * @param {number} versionDelta - Amount to increment version (default 0)
 * @returns {Object} { nextState, isValid, validationErrors }
 */
export function computeStateUpdate(prevState, update, versionDelta = 0) {
  // Resolve update (handle both objects and functions)
  const updateValue = typeof update === 'function' ? update(prevState) : update;
  let candidateState = { ...prevState, ...updateValue };

  if (
    candidateState.lookingFor === 'product'
    && prevState.lookingFor !== 'product'
    && SHOW_LOGISTICS_DETAILS_WHEN_DISTRIBUTION.has(candidateState.distribution)
  ) {
    candidateState = {
      ...candidateState,
      distribution: null,
      eventType: null,
      logisticsVideo: false,
      logisticsInPerson: false,
      logisticsLive: false,
      logisticsPreRecorded: false,
      logisticsRemote: false,
      logisticsUrl: '',
      equipmentNotes: '',
    };
  }

  // Validate
  const errors = validationErrors(candidateState);
  const isValid = Object.keys(errors).length === 0;

  if (!isValid) {
    // Validation failed: apply user's input changes, but track errors for display
    // This allows the user to type freely while seeing validation feedback
    return {
      nextState: { ...candidateState, version: prevState.version + versionDelta, errors },
      isValid: false,
      validationErrors: errors,
    };
  }

  // Validation passed: apply update and increment version
  return {
    nextState: {
      ...candidateState,
      version: prevState.version + versionDelta,
      errors: {}, // Clear any previous errors
    },
    isValid: true,
    validationErrors: {},
  };
}

export default function useSyncedState(requestId = null, copyFromId = null, targetReporterProfileId = null, returnUrl = '/reporter_requests', viewRequestUrlTemplate = null, initialOverrides = {}) {
  // Determine form mode: "edit" if loading existing request, "create" for new request
  // This is a stable constant derived from the prop, not reactive state
  const formMode = requestId ? 'edit' : 'create';
  const idToLoad = requestId || copyFromId;
  const isCopying = !!copyFromId && !requestId;

  // Generate deduplication key for this draft session to prevent duplicate creation
  // on retries. Key is regenerated on each page load (new form = new intent).
  const deduplicationKeyRef = useRef(null);
  if (!deduplicationKeyRef.current) {
    deduplicationKeyRef.current = generateUuid();
  }

  const autoSaveTimerRef = useRef(null);
  const saveInProgressRef = useRef(null);

  const persistedDefaults = useMemo(() => {
    const persistedValue = window.localStorage.getItem(LOCAL_STORAGE_PERSISTED_DEFAULTS_TAG);
    return parsePersistedDefaults(persistedValue);
  }, []);

  const updatePersistedDefaults = useCallback((values) => {
    window.localStorage.setItem(
      LOCAL_STORAGE_PERSISTED_DEFAULTS_TAG,
      JSON.stringify(_.pick(values, LOCAL_STORAGE_PERSISTED_DEFAULTS_KEYS)),
    );
  }, []);

  const [params, setParams] = useState({
    ...getInitialState(),
    ...(formMode === 'create' && !copyFromId ? persistedDefaults : {}),
    ...(formMode === 'create' && !copyFromId ? _.pick(initialOverrides, ['title', 'details', 'lookingFor']) : {}),
    formMode,
    currentUiState: idToLoad ? 'loading' : 'form-revealed',
  });

  const updateParamsStateWithValidation = useCallback(
    (f, versionDelta, callback) => {
      setParams((prevParams) => {
        const { nextState, isValid, validationErrors: errors } = computeStateUpdate(prevParams, f, versionDelta ?? 0);

        // Side effects (only after pure computation)
        if (!isValid) {
          qlog.warn('Validation failed:', errors);
        } else if (callback) {
          callback(nextState);
        }

        return nextState;
      });
    },
    [setParams],
  );

  const derivedParams = useMemo(() => ({
    ...populateDerivedState(params),
    viewRequestUrlTemplate,
  }), [params, viewRequestUrlTemplate]);

  const scrollToFirstError = useCallback((errors) => {
    const fieldToSectionId = {
      lookingFor: 'request-type',
      title: 'request-details',
      details: 'request-details',
      mediaOutletId: 'media-distribution',
      eventType: 'media-distribution',
      logisticsInPerson: 'logistic-details',
      logisticsLive: 'logistic-details',
      logisticsPreRecorded: 'logistic-details',
      logisticsRemote: 'logistic-details',
      logisticsVideo: 'logistic-details',
      deadline: 'deadline',
      customDeadlineDateTime: 'deadline',
      customScheduleDateTime: 'deadline',
    };

    const firstErrorField = Object.keys(errors)[0];
    const sectionId = fieldToSectionId[firstErrorField];

    if (sectionId) {
      const section = document.getElementById(sectionId);
      section?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus first invalid input after scroll completes
      setTimeout(() => {
        const firstInvalid = section?.querySelector('.is-invalid');
        firstInvalid?.focus();
      }, 300);
    }
  }, []);

  const updateAbortControllerRef = useRef(null);
  const errorsToScrollRef = useRef(null);

  const abortUpdateRequest = useCallback(() => {
    if (updateAbortControllerRef.current) {
      updateAbortControllerRef.current.abort();
      updateAbortControllerRef.current = null;
    }
  }, []);

  const loadRequest = useCallback(async () => {
    setParams((prev) => ({ ...prev, currentUiState: 'loading' }));
    try {
      const data = await qapi.requests.get(idToLoad);
      if (!data) {
        qlog.error('No request data returned from API');
        setParams((prev) => ({ ...prev, currentUiState: 'loading-failed' }));
        return;
      }
      const deserialized = deserializeSourceRequest(data);

      // Copy mode: reset fields for new request
      if (isCopying) {
        const maxLen = 150;
        const prefix = 'Copy of ';
        deserialized.title = prefix + deserialized.title.substring(0, maxLen - prefix.length);
        deserialized.sourceRequestId = null; // Force CREATE
        deserialized.deadline = '7days';
        deserialized.customDeadlineDateTime = '';
        deserialized.schedule = 'now';
        deserialized.customScheduleDateTime = '';
        deserialized.requestStatus = null;
      }

      setParams((prev) => ({
        ...prev,
        ...deserialized,
        currentUiState: 'form-revealed',
      }));
    } catch (err) {
      qlog.error(isCopying ? 'Failed to load request for copying:' : 'Failed to load request for editing:', err);
      setParams((prev) => ({ ...prev, currentUiState: 'loading-failed' }));
    }
  }, [idToLoad, isCopying]);

  // Load existing request data on mount in edit mode or copy mode
  useEffect(() => {
    if (idToLoad) {
      loadRequest();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const executeSaveRequest = useCallback(
    async (derivedSystemParams, extras, { saveOrigin = 'auto' } = {}) => {
      const isUpdate = !!derivedSystemParams.sourceRequestId;

      if (isUpdate) {
        abortUpdateRequest();
        updateAbortControllerRef.current = new AbortController();
      }

      const serialized = { ...serializeSourceRequest(derivedSystemParams), ...extras };

      // Include deduplication key for CREATE requests to prevent duplicate drafts on retries
      if (!isUpdate) {
        serialized.deduplication_key = deduplicationKeyRef.current;

        // Include target reporter profile ID when admin is duplicating another user's request
        if (targetReporterProfileId) {
          serialized.reporter_profile_migrate_id = targetReporterProfileId;
        }
      }

      try {
        updateParamsStateWithValidation((prevParams) => ({ ...prevParams, savingState: 'saving' }));
        // Clear previous error state when starting a new save
        setParams((prevParams) => ({ ...prevParams, savingError: null }));
        const apiResponse = await (isUpdate
          ? qapi.requests.update(
              derivedSystemParams.sourceRequestId,
              serialized,
              updateAbortControllerRef.current.signal,
            )
          : qapi.requests.create(serialized));
        const {
 sourceRequestId, slug, migrateId, requestStatus,
} = deserializeSourceRequest(apiResponse);
        updateParamsStateWithValidation((prevParams) => {
          const nextParams = {
 ...prevParams, sourceRequestId, slug, migrateId, requestStatus,
};

          return {
            ...nextParams,
            lastSavedVersion: nextParams.version,
            savingState: 'idle',
            validationActive: true, // Activate validation after successful save
          };
        });
        // Set lastSavedAt using setParams directly to avoid triggering auto-saves.
        // This mirrors how tickTime() updates timeNow without incrementing version.
        setParams((prevParams) => ({
          ...prevParams,
          lastSavedAt: prevParams.timeNow,
          savingError: null,
        }));
        return { success: true, sourceRequestId, migrateId };
      } catch (error) {
        updateParamsStateWithValidation((prevParams) => ({ ...prevParams, savingState: 'idle' }));
        // Ignore abort errors (normal when new save cancels previous one).
        // Only set savingError for actual network/API failures.
        if (error?.name !== 'AbortError') {
          setParams((prevParams) => ({ ...prevParams, savingError: saveOrigin }));
          // Show toast notification for manual save failures (user-initiated)
          if (saveOrigin === 'manual') {
            let detailedErrorMessage = error?.message;
            if (detailedErrorMessage) {
              detailedErrorMessage = ` (${detailedErrorMessage})`;
            }

            window.Qwoted?.showToast?.({
              toastTitle: 'Error',
              toastBody: `Saving failed${detailedErrorMessage}. Please try again.`,
              variant: 'danger',
            });
          }
        }
        return { success: false, sourceRequestId: null };
      }
    },
    [abortUpdateRequest, updateParamsStateWithValidation, targetReporterProfileId],
  );

  // Serialize save operations to prevent race conditions
  const saveRequest = useCallback(
    async (derivedSystemParams, extras, options) => {
      // Wait for in-flight save to complete
      if (saveInProgressRef.current) {
        await saveInProgressRef.current;
      }

      const savePromise = executeSaveRequest(derivedSystemParams, extras, options);
      saveInProgressRef.current = savePromise;

      try {
        const result = await savePromise;
        return result;
      } finally {
        if (saveInProgressRef.current === savePromise) {
          saveInProgressRef.current = null;
        }
      }
    },
    [executeSaveRequest],
  );

  const saveDraftAndRedirect = useCallback(async () => {
    // Cancel any pending auto-save to avoid waiting on in-flight save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Check if draft requirements are met (with validation active)
    // Use 'draft' level: only validate title + description minimum
    const draftErrors = validationErrors({ ...derivedParams, validationActive: true }, 'draft');

    if (Object.keys(draftErrors).length > 0) {
      // Validation failed - activate validation and scroll to first error
      // Always trigger scroll, even if validation is already active
      // (user may have partially fixed errors and clicked again)
      errorsToScrollRef.current = draftErrors;

      // Always update state to ensure re-render and scroll
      setParams((prevParams) => ({
        ...prevParams,
        validationActive: true,
        errors: draftErrors,
      }));
      return; // Block save
    }

    // Validation passed - proceed with save
    const { success } = await saveRequest(derivedParams, {}, { saveOrigin: 'manual' });
    if (success) {
      window.location.href = returnUrl;
    }
  }, [saveRequest, derivedParams, setParams, returnUrl]);

  // Auto-save is triggered by watching version, NOT the full derivedParams.
  // Why? Because derivedParams also includes timeNow, which updates every second.
  // We only want to save when the user makes a meaningful change (version increment),
  // not when time ticks. This keeps saves grouped by user actions, not by time.
  // See: tickTime() intentionally doesn't increment version.
  useEffect(() => {
    const isLiveRequest = derivedParams.requestStatus === 'scheduled' || derivedParams.requestStatus === 'posted';
    if (isLiveRequest || !isReadyForDraftSaving(derivedParams)) return () => {};

    abortUpdateRequest();

    updateParamsStateWithValidation((prevParams) => {
      if (prevParams.savingState === 'pending') {
        return prevParams; // Same ref = React skips re-render
      }
      return { ...prevParams, savingState: 'pending' };
    });

    autoSaveTimerRef.current = setTimeout(() => {
      if (!autoSaveTimerRef.current) return;

      saveRequest(derivedParams);
      autoSaveTimerRef.current = null;
    }, 5000);

    return () => {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedParams.version]);

  const saveAndExit = useCallback(async () => {
    // Cancel any pending auto-save to avoid waiting on in-flight save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // For scheduled/posted requests: save with full validation (strict as publishing)
    const fullErrors = validationErrors({ ...derivedParams, validationActive: true });

    if (Object.keys(fullErrors).length > 0) {
      // Validation failed - activate validation and scroll to first error
      errorsToScrollRef.current = fullErrors;
      setParams((prevParams) => ({
        ...prevParams,
        validationActive: true,
        errors: fullErrors,
      }));
      return; // Block save
    }

    // When editing a scheduled request and user sets schedule to "now",
    // post immediately instead of saving (which would unschedule to draft)
    const isScheduledAndNowSelected = derivedParams.requestStatus === 'scheduled' && derivedParams.schedule === 'now';

    const extras = isScheduledAndNowSelected ? { post: true } : {};

    // Validation passed - save and redirect to my_requests
    const { success } = await saveRequest(derivedParams, extras, { saveOrigin: 'manual' });
    if (success) {
      window.location.href = returnUrl;
    }
  }, [saveRequest, derivedParams, setParams, returnUrl]);

  const abandonChangesAndExit = useCallback(() => {
    // Discard unsaved changes and navigate away
    // Auto-save was disabled for live requests, so server-side data is unchanged
    window.location.href = returnUrl;
  }, [returnUrl]);

  const publishNow = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    const fullErrors = validationErrors({ ...derivedParams, validationActive: true });

    if (Object.keys(fullErrors).length > 0) {
      errorsToScrollRef.current = fullErrors;
      setParams((prevParams) => ({
        ...prevParams,
        validationActive: true,
        errors: fullErrors,
      }));
      return;
    }

    try {
      const { success, migrateId: savedMigrateId } = await saveRequest(
        derivedParams,
        { post: true },
        { saveOrigin: 'manual' },
      );

      if (!success) {
        qlog.error('Publish request failed');
        return;
      }

      if (V1_FEATURES.SKIP_REVIEW_AND_THANKS_PAGES && viewRequestUrlTemplate) {
        const isScheduled = derivedParams.schedule === 'custom' && derivedParams.customScheduleDateTime;
        const endpoint = isScheduled ? 'scheduled' : 'thanks';
        const redirectUrl = `${viewRequestUrlTemplate.replace(':id', savedMigrateId)}/${endpoint}`;
        window.location.href = redirectUrl;
      } else {
        updateParamsStateWithValidation((prevParams) => ({
          ...prevParams,
          currentUiState: 'posted',
        }));
      }
    } catch (error) {
      qlog.error('Publish request failed:', error);
    }
  }, [saveRequest, derivedParams, setParams, updateParamsStateWithValidation, viewRequestUrlTemplate]);

  const goToReview = useCallback(async () => {
    // Cancel auto-save to prevent duplicate creation
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Check if full validation requirements are met (with validation active)
    const fullErrors = validationErrors({ ...derivedParams, validationActive: true });

    if (Object.keys(fullErrors).length > 0) {
      // Validation failed - activate validation and scroll to first error
      errorsToScrollRef.current = fullErrors;

      // Always update state to ensure re-render and scroll
      setParams((prevParams) => ({
        ...prevParams,
        validationActive: true,
        errors: fullErrors,
      }));
      return; // Block transition to review
    }

    // Validation passed - save as draft and proceed to review screen
    try {
      const { success } = await saveRequest(derivedParams, {}, { saveOrigin: 'manual' });
      if (success) {
        // Transition to reviewing state
        updateParamsStateWithValidation((prevParams) => ({
          ...prevParams,
          currentUiState: 'reviewing',
        }));
      }
    } catch (error) {
      qlog.error('Save for review failed:', error);
    }
  }, [saveRequest, derivedParams, setParams, updateParamsStateWithValidation]);

  const goBackToEditing = useCallback(() => {
    setParams((prevParams) => ({
      ...prevParams,
      currentUiState: 'form-revealed',
    }));
  }, [setParams]);

  const updateUIParamsWithValidation = useCallback(
    (f) => updateParamsStateWithValidation(f, 1, updatePersistedDefaults),
    [updateParamsStateWithValidation, updatePersistedDefaults],
  );

  const updateParam = useCallback(
    (name, value) => updateUIParamsWithValidation({ [name]: value }),
    [updateUIParamsWithValidation],
  );

  const toggleParam = useCallback(
    (name) => updateUIParamsWithValidation((prevParams) => ({
      [name]: !prevParams[name],
      touchedFields: { ...prevParams.touchedFields, [name]: true },
    })),
    [updateUIParamsWithValidation],
  );

  const useUpdateParamWithEvent = (name) => useCallback(
      (event) => {
        if (event.target.type === 'checkbox') {
          updateParam(name, event.target.checked);
        } else {
          updateParam(name, event.target.value);
        }
      },
      [name],
    );

  const markFieldTouched = useCallback(
    (fieldName) => {
      updateParamsStateWithValidation(
        (prevParams) => ({
          ...prevParams,
          touchedFields: { ...prevParams.touchedFields, [fieldName]: true },
        }),
        0, // versionDelta=0 so blur doesn't trigger auto-save
      );
    },
    [updateParamsStateWithValidation],
  );

  // Time ticks every second but does NOT increment version.
  // Why? Because we want timeNow to update in derived state (for deadline countdown display)
  // without triggering auto-saves just because a second passed.
  // Only user interactions increment version and trigger saves.
  // Validation is also skipped here to keep error messages from user interactions intact.
  const tickTime = useCallback((timeNow) => setParams((prevParams) => ({ ...prevParams, timeNow })), [setParams]);

  // Scroll to error after React re-renders with validation active
  // This must happen in useEffect because scrollToFirstError needs the DOM to have .is-invalid classes
  useEffect(() => {
    if (errorsToScrollRef.current && params.validationActive) {
      scrollToFirstError(errorsToScrollRef.current);
      errorsToScrollRef.current = null; // Clear after scrolling
    }
  }, [params, scrollToFirstError, derivedParams]);

  return {
    abandonChangesAndExit,
    backToEditing: goBackToEditing,
    derivedParams,
    markFieldTouched,
    postRequest: V1_FEATURES.SKIP_REVIEW_AND_THANKS_PAGES ? publishNow : goToReview,
    publishNow,
    retryLoad: loadRequest,
    saveDraft: saveDraftAndRedirect,
    saveAndExit,
    tickTime,
    toggleParam,
    updateParam,
    useUpdateParamWithEvent,
  };
}
